import React, { useState, useEffect, useRef } from "react";
import { Modal } from "../Modal/Modal";
import { CommissionContract, CommissionComment } from "../../api/ComissionContractsAPI";
import ComissionContractsAPI from "../../api/ComissionContractsAPI";
import ConfigAPI from "../../api/ConfigAPI";
import SectionTitle from "../SectionTitle/SectionTitle";
import InputShortText from "../InputShortText/InputShortText";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";
import InputNumber from "../InputNumber/InputNumber";
import SelectInput from "../SelectInput/SelectInput";
import TextArea from "../TextArea/TextArea";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import NoData from "../NoData/NoData";
import { PrivateResourceLink } from "../PrivateResourceLink/PrivateResourceLink";
import { CommissionStatus } from "../../types/commission-statuses";
import { PaymentStatus } from "../../types/payment-statuses";
import { Download, ChevronDown, ChevronUp } from "lucide-react";
import CopyText from "../CopyText/CopyText";
import { toastService } from "../../services/ToastService";
import CommentCard from "../CommentCard/CommentCard";
import PriceField from "../PriceField/PriceField";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import CONFIG from "../../config/config";
import { useTranslation } from "react-i18next";
import { commissionStatusToTranslationKey } from "../../utils/commission-status";
import { paymentStatusToTranslationKey } from "../../utils/payment-status";
import Switch from "../Switch/Switch";
import PaymentPlanButton from "../PaymentPlanButton/PaymentPlanButton";
import CommissionPaymentPlanManager, { CommissionPaymentPlanManagerRef } from "../CommissionPaymentPlan/CommissionPaymentPlanManager";
import { useCommissionContractTemplate } from "../../hooks/useCommissionContractTemplate";
import CommissionPaymentPlansAPI, { toFrontend as commissionPlanToFrontend } from "../../api/CommissionPaymentPlansAPI";
import SlideOver from "../SlideOver/SlideOver";
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import { ExternalLink } from "lucide-react";
import FileUploadWrapper from "../FileUploadWrapper/FileUploadWrapper";
import FilesAPI from "../../api/FilesAPI";
import { urlAndS3Encode, isLargeFile } from "../../shared/fileUtils";
import { simplifyUrl } from "../../shared/urlUtils";
import ListContainer from "../ListContainer/ListContainer";
import ElementActions from "../ElementActions/ElementActions";
import IconButton from "../IconButton/IconButton";
import binIcon from "../../assets/images/bin.svg";
import { PaymentPlanItem, PaymentConcept, PAYMENT_CONCEPT_LABELS, PaymentStatus as PaymentPlanStatus, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from "../../types/payment-plan";
import { CommissionPaymentPlan } from "../../types/commissionPaymentPlan";
import { isValidText } from "../../shared/validation";
import Notice from "../Notice/Notice";
interface StoreCommissionPanelProps {
  commission: CommissionContract;
  onClose: () => void;
  isEdit?: boolean;
  onSave?: (commission: CommissionContract) => void;
}

function StoreCommissionPanel({ commission, onClose, isEdit, onSave }: StoreCommissionPanelProps) {
  const { t } = useTranslation();
  const { contractTemplate } = useCommissionContractTemplate();
  const [comment, setComment] = useState('');
  const [isEditing, setIsEditing] = useState(isEdit);
  const [comments, setComments] = useState<CommissionComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [storeName, setStoreName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [showPaymentPlanManager, setShowPaymentPlanManager] = useState(false);
  const [, setIsEditMode] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState<CommissionPaymentPlan | undefined>((commission as any).paymentPlan);
  const paymentPlanManagerRef = useRef<CommissionPaymentPlanManagerRef>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showDamagesNotes, setShowDamagesNotes] = useState(false);

  useEffect(() => {
    setIsEditing(isEdit);
  }, [isEdit]);

  const [form, setForm] = useState<CommissionContract>({ ...commission });

  useEffect(() => {
    setForm({ ...commission });
  }, [commission]);

  useEffect(() => {
    if (commission.id) {
      fetchComments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commission.id]);

  useEffect(() => {
    const fetchStoreName = async () => {
      try {
        const response = await ConfigAPI.get();
        setStoreName(response.data.brandName || '');
      } catch (error) {
        console.error('Failed to fetch store name:', error);
      }
    };
    fetchStoreName();
  }, []);

  useEffect(() => {
    const fetchPaymentPlan = async () => {
      if (!commission.id) return;
        const resp = await CommissionPaymentPlansAPI.get(commission.id);
        if (resp.data) {
          setPaymentPlan(commissionPlanToFrontend(resp.data));
        }
    };
    fetchPaymentPlan();
  }, [commission.id]);

  const shareLink = `${window.location.protocol}//${window.location.host}/drops/track/${commission.trackingId ?? ''}`;

  const latestContract = commission.contracts && commission.contracts.length > 0
    ? [...commission.contracts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    : null;

  const fetchComments = async () => {
    if (!commission.id) return;
    try {
      const response = await ComissionContractsAPI.getComments(commission.id);
      setComments(response.data);
    } catch (error) {
      toastService.showToast(t('store_commission_panel.toast.comments_load_error') || 'Failed to load comments', 'error');
    }
  };

  const handleAddComment = async () => {
    if (!commission.id || !comment.trim()) return;
    
    setIsLoading(true);
    try {
      await ComissionContractsAPI.addComment(commission.id, {
        comment: comment.trim(),
        writtenBy: storeName
      });
      setComment('');
      await fetchComments();
      toastService.showToast(t('store_commission_panel.toast.comment_added') || 'Comment added', 'success');
    } catch (error) {
      toastService.showToast(t('store_commission_panel.toast.comment_error') || 'Failed to add comment', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(form);
      onClose();
    }
  };

  const handlePaymentPlanSave = async (newPaymentPlan: CommissionPaymentPlan): Promise<void> => {
    try {
      if (!commission.id) return;
      if (!paymentPlan || !paymentPlan.id) {
        const resp = await CommissionPaymentPlansAPI.create(commission.id, newPaymentPlan);
        setPaymentPlan(commissionPlanToFrontend(resp.data));
      } else {
        const resp = await CommissionPaymentPlansAPI.update(commission.id, newPaymentPlan);
        setPaymentPlan(commissionPlanToFrontend(resp.data));
      }
      setShowPaymentPlanManager(false);
      setIsEditMode(false);
      toastService.showToast(t("store_commission_panel.payment_plan_updated") || "Payment plan updated successfully", "success");
    } catch (e: any) {
      toastService.showToast(t("payment_plan.errors.failed_to_save") || 'Failed to save payment plan', 'error');
    }
  };

  const handlePaymentPlanCancel = () => {
    setShowPaymentPlanManager(false);
    setIsEditMode(false);
  };

  const handlePaymentPlanEdit = () => {
    setIsEditMode(true);
    setShowPaymentPlanManager(true);
  };


  const handleAddRequiredPayment = () => {
    setPaymentPlan(prev => {
      const templatePrice = contractTemplate?.dropFee?.price ?? 0;
      const templateCurrency = contractTemplate?.dropFee?.currency || 'EUR';
      const currency = templateCurrency || prev?.currency || commission.desiredSalePriceCurrency || 'EUR';
      const base: CommissionPaymentPlan = prev || {
        id: null, // Use null instead of empty string
        contractId: commission.id || 0,
        items: [],
        totalAmount: 0,
        currency,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const newItem: PaymentPlanItem = {
        id: null,
        concept: PaymentConcept.RENEWAL_FEE,
        amount: templatePrice,
        currency,
        status: 'NOT_PAID' as any,
        isRequired: true,
        validForDays: contractTemplate?.saleDuration,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        description: ''
      };
      return { ...base, items: [...base.items, newItem], updatedAt: new Date().toISOString() };
    });
  };

  const handleDeletePaymentSplit = async (index: number) => {
    if (!paymentPlan || !commission.id) return;
    
    try {
      const updatedItems = paymentPlan.items.filter((_, i) => i !== index);
      const updatedPlan: CommissionPaymentPlan = {
        ...paymentPlan,
        items: updatedItems,
        totalAmount: updatedItems.reduce((sum, item) => sum + item.amount, 0),
        updatedAt: new Date().toISOString()
      };
      
      setPaymentPlan(updatedPlan);
      
      const resp = await CommissionPaymentPlansAPI.update(commission.id, updatedPlan);
      setPaymentPlan(commissionPlanToFrontend(resp.data));
    } catch (e: any) {
      setPaymentPlan(paymentPlan);
      toastService.showToast(t("global.error") || "Failed to delete payment split", "error");
    }
  };

  const removePhoto = (field: 'attachments', fileName: string) => {
    if (!form) return;
    setForm({
      ...form,
      [field]: (form[field] || []).filter((f: string) => f !== fileName)
    });
  };

  const handleFileChange = async (field: 'attachments', files: File[]) => {
    if (!form || !commission.trackingId) return;
    
    setIsUploading(true);
    try {
      const uploadedUrls: string[] = [];
      
      for (const file of files) {
        try {
          const fileWithS3Encoding = urlAndS3Encode(file.name);
          const displayFileName = decodeURI(fileWithS3Encoding);
          
          let fileUrl: string;
          
          if (isLargeFile(file)) {
            const uploadUrlResponse = await FilesAPI.getUploadUrl(fileWithS3Encoding, 'commission-contracts', commission.trackingId);
            const uploadUrl = uploadUrlResponse.data.url;
            const response = await FilesAPI.uploadHeavyFile(uploadUrl, file, fileWithS3Encoding);
            if (response.status === 200) {
              fileUrl = simplifyUrl(uploadUrl);
            } else {
              throw new Error('Failed to upload large file');
            }
          } else {
            // Use regular upload for smaller files
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await FilesAPI.upload(`/v1/files/commission-contracts/uploadV2?prefixId=${commission.trackingId}`, fileWithS3Encoding, formData);
            fileUrl = response.data.url;
          }
          
          uploadedUrls.push(fileUrl);
          toastService.showToast(`${t("toast.success.add_file") || 'File added'}: ${displayFileName}`, 'success');
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          toastService.showToast(`${t("toast.errors.add_file") || 'Failed to add file'}: ${file.name}`, 'error');
        }
      }
      
      // Update form with new file URLs
      if (uploadedUrls.length > 0) {
        setForm({
          ...form,
          [field]: [...(form[field] || []), ...uploadedUrls]
        });
      }
    } catch (error) {
      console.error('Error processing files:', error);
      toastService.showToast(t('store_commission_panel.errors.failed_to_upload_files') || 'Failed to upload files', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleNumberChange = (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Modal modalTitle={t("store_commission_panel.modal_title") || "Commission details"} canClose={true} onClose={onClose}>
      <>
        <div>
          <div className="flex flex-col md:flex-row gap-6">
          {/* Left: Commission data */}
          <div className="flex-1 space-y-4 overflow-y-auto max-h-[80vh] pr-2">
            {commission.trackingId && (
              <div className="flex items-center gap-3 mb-2">
                <CopyText text={shareLink} />
                <span className="text-xs text-muted-foreground mr-2">{t("store_commission_panel.share_with_customer") || "Share with the customer"}</span>
                {latestContract && latestContract.pdf && (
                  <a
                    href={latestContract.pdf}
                    download
                    className="flex items-center text-xs text-blue-600 underline hover:text-blue-800 ml-2 gap-1"
                  >
                    <Download className="h-4 w-4" />
                    {t("store_commission_panel.download_pdf_contract") || "Download PDF contract"}
                  </a>
                )}
              </div>
            )}
            <SectionTitle text={t("store_commission_panel.bride_data_title") || "Bride data"} />
            {/* Name and surname in one line */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <InputShortText 
                label={t("store_commission_panel.first_name") || "First name"} 
                defaultValue={isEditing ? form.brideName : commission.brideName} 
                onChange={isEditing ? handleInputChange('brideName') : (e => {})} 
                isDisabled={!isEditing} 
              />
              <InputShortText 
                label={t("store_commission_panel.last_name") || "Last name"} 
                defaultValue={isEditing ? form.brideSurname : commission.brideSurname} 
                onChange={isEditing ? handleInputChange('brideSurname') : (e => {})} 
                isDisabled={!isEditing} 
              />
            </div>
            {/* Phone number in one line */}
            <div className="mb-2">
              {isEditing ? (
                <PhoneNumberInput
                  label={t("store_commission_panel.phone") || "Phone"}
                  defaultValue={form.bridePhone}
                  onChange={value => setForm(prev => ({ ...prev, bridePhone: value }))}
                  isDisabled={false}
                />
              ) : (
                <InputShortText label={t("store_commission_panel.phone") || "Phone"} defaultValue={commission.bridePhone || ''} onChange={e => {}} isDisabled />
              )}
            </div>
            {/* Email and social media together */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <InputShortText label={t("store_commission_panel.email") || "Email"} defaultValue={isEditing ? form.brideEmail : commission.brideEmail || ''} onChange={isEditing ? handleInputChange('brideEmail') : (e => {})} isDisabled={!isEditing} />
              <InputShortText label={t("store_commission_panel.social_media") || "Social media"} defaultValue={isEditing ? form.brideSocial : commission.brideSocial || ''} onChange={isEditing ? handleInputChange('brideSocial') : (e => {})} isDisabled={!isEditing} />
            </div>
            <SectionTitle text={t("store_commission_panel.item_data_title") || "Item data"} />
            {/* Model name in one line */}
            <div className="mb-2">
              <InputShortText label={t("store_commission_panel.dress_model") || "Dress model"} defaultValue={isEditing ? form.dress : commission.dress} onChange={isEditing ? handleInputChange('dress') : (e => {})} isDisabled={!isEditing} />
            </div>
            {/* Rozmiar and Rok zakupu together */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <InputShortText label={t("store_commission_panel.dress_size") || "Dress size"} defaultValue={isEditing ? form.dressSize : commission.dressSize || ''} onChange={isEditing ? handleInputChange('dressSize') : (e => {})} isDisabled={!isEditing} />
              <InputNumber 
                label={t("store_commission_panel.purchase_year") || "Purchase year"} 
                defaultValue={isEditing ? (form.purchaseYear ? parseInt(form.purchaseYear) : undefined) : (commission.purchaseYear ? parseInt(commission.purchaseYear) : undefined)} 
                onChange={isEditing ? handleNumberChange('purchaseYear') : (e => {})} 
                isDisabled={!isEditing}
                validationFn={(value) => value >= 1900 && value <= new Date().getFullYear()}
                errorMessage="errors.invalid_year"
              />
            </div>
            {/* Original price and sale price range together */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <PriceField
                label={t("store_commission_panel.original_price") || "Original price"}
                currency={isEditing ? form.purchasePriceCurrency || 'EUR' : commission.purchasePriceCurrency || 'EUR'}
                price={isEditing ? (form.purchasePriceAmount || 0) : (commission.purchasePriceAmount || 0)}
                currencies={CONFIG.SUPPORTED_CURRENCIES}
                onCurrencyChange={(currency) => setForm(prev => ({ ...prev, purchasePriceCurrency: currency }))}
                onPriceChange={isEditing ? (price) => setForm(prev => ({ ...prev, purchasePriceAmount: price })) : () => {}}
                isDisabled={!isEditing}
              />
              <PriceField
                label={t("store_commission_panel.sale_price_range") || "Sale price range"}
                currency={isEditing ? form.desiredSalePriceCurrency || 'EUR' : commission.desiredSalePriceCurrency || 'EUR'}
                price={isEditing ? (form.desiredSalePriceAmount || 0) : (commission.desiredSalePriceAmount || 0)}
                currencies={CONFIG.SUPPORTED_CURRENCIES}
                onCurrencyChange={(currency) => setForm(prev => ({ ...prev, desiredSalePriceCurrency: currency }))}
                onPriceChange={isEditing ? (price) => setForm(prev => ({ ...prev, desiredSalePriceAmount: price })) : () => {}}
                isDisabled={!isEditing}
              />
            </div>
            
            {/* Collapsible Notes Section */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowNotes(!showNotes)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline mb-2"
              >
                {showNotes ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t("store_commission_panel.hide_notes") || "Hide notes"}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t("store_commission_panel.show_notes") || "Show notes"}
                  </>
                )}
              </button>
              
              {showNotes && (
                <div className="mt-2">
                  <TextArea
                    label={t("store_commission_panel.dress_measurements") || "Dress measurements"}
                    defaultValue={isEditing ? (form.notes || '') : (commission.notes || '')}
                    onChange={isEditing ? (e) => setForm(prev => ({ ...prev, notes: e.target.value })) : (e => {})}
                    isDisabled={!isEditing}
                    helperText={t("store_commission_panel.dress_measurements_helper") || "Enter exact dress measurements to help match future clients"}
                  />
                </div>
              )}
            </div>

            {/* Collapsible Damages Notes Section */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowDamagesNotes(!showDamagesNotes)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline mb-2"
              >
                {showDamagesNotes ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t("store_commission_panel.hide_damages_notes") || "Hide damages notes"}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t("store_commission_panel.show_damages_notes") || "Show damages notes"}
                  </>
                )}
              </button>
              
              {showDamagesNotes && (
                <div className="mt-2">
                  <TextArea
                    label={t("store_commission_panel.damages_notes") || "Damages Notes"}
                    defaultValue={isEditing ? (form.damagesNotes || '') : (commission.damagesNotes || '')}
                    onChange={isEditing ? (e) => setForm(prev => ({ ...prev, damagesNotes: e.target.value })) : (e => {})}
                    isDisabled={!isEditing}
                    placeholder={t("store_commission_panel.damages_notes_placeholder") || "Describe any damages or wear on the dress..."}
                    maxLength={500}
                  />
                </div>
              )}
            </div>
            
            {/* Item received switch */}
            <div className="mb-4">
              <div className="flex items-center space-x-3">
                <Switch
                  checked={isEditing ? form.itemReceived : commission.itemReceived}
                  onChange={isEditing ? () => setForm(prev => ({ ...prev, itemReceived: !prev.itemReceived })) : () => {}}
                  disabled={!isEditing}
                />
                <label className="text-sm font-medium text-gray-700">
                  {t("store_commission_panel.item_received") || "Item (dress) received"}
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t("store_commission_panel.item_received_helper") || "Toggle this switch when the physical dress has been received by the store."}
              </p>
            </div>
            
            <SectionTitle text={t("store_commission_panel.order_data_title") || "Order data"} />
            {commission.trackingId && commission.status !== CommissionStatus.ACCEPTED && (
              <Notice
                pillText={t("store_commission_panel.pdf_notice.pill") || "Info"}
                tinyText={t("store_commission_panel.pdf_notice.message") || "The customer won't be able to download the PDF unless the request is accepted."}
              />
            )}
            {/* Status */}
            <div className="grid grid-cols-1 gap-2 mb-2">
              {isEditing ? (
                <SelectInput
                  label={t("store_commission_panel.status") || "Status"}
                  values={Object.values(CommissionStatus).map(v => ({ 
                    value: v, 
                    readableValue: t(commissionStatusToTranslationKey(v)) || v
                  }))}
                  defaultValue={form.status}
                  onChange={handleInputChange('status')}
                />
              ) : (
                <InputShortText 
                  label={t("store_commission_panel.status") || "Status"} 
                  defaultValue={t(commissionStatusToTranslationKey(form.status as CommissionStatus)) || form.status} 
                  onChange={e => {}} 
                  isDisabled 
                />
              )}
              
            </div>
            {/* End date */}
            <div className="grid grid-cols-1 gap-2 mb-2">
              <CustomDatePicker
                label={t("store_commission_panel.end_date") || "End date"}
                value={isEditing ? (form.endDate ? new Date(form.endDate) : null) : (commission.endDate ? new Date(commission.endDate) : null)}
                onChange={(date) => setForm(prev => ({ ...prev, endDate: date ? date.toISOString().split('T')[0] : '' }))}
                isDisabled={!isEditing}
                dateFormat="dd/MM/yyyy"
                showTimeSelect={false}
              />
            </div>
            {/* Contract number and reference */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <InputShortText 
                label={t("store_commission_panel.contract_number") || "Contract number"} 
                defaultValue={isEditing ? (form.contractNumber || '') : (commission.contractNumber || '')} 
                onChange={isEditing ? handleInputChange('contractNumber') : (e => {})} 
                isDisabled={!isEditing}
                validationFn={(value) => !!value && isValidText(value, 0, 50)}
                errorMessage="errors.long_short"
              />
              <InputShortText 
                label={t("store_commission_panel.contract_reference") || "Contract reference"} 
                defaultValue={isEditing ? (form.contractReference || '') : (commission.contractReference || '')} 
                onChange={isEditing ? handleInputChange('contractReference') : (e => {})} 
                isDisabled={!isEditing}
                validationFn={(value) => !!value && isValidText(value, 0, 50)}
                errorMessage="errors.long_short"
              />
            </div>
            {commission.id && (
              <div className="mt-3">
                <SectionTitle text={t('store_commission_panel.payments_title') || 'Payments'} />
                <div className="mt-2 space-y-2">
                  {/* Payment status moved under Payments section */}
                  {isEditing ? (
                    <SelectInput
                      label={t("store_commission_panel.payment_status") || "Payment status"}
                      values={Object.values(PaymentStatus).map(v => ({ 
                        value: v, 
                        readableValue: t(paymentStatusToTranslationKey(v)) || v
                      }))}
                      defaultValue={form.payment}
                      onChange={handleInputChange('payment')}
                    />
                  ) : (
                    <InputShortText 
                      label={t("store_commission_panel.payment_status") || "Payment status"} 
                      defaultValue={t(paymentStatusToTranslationKey(form.payment as PaymentStatus)) || form.payment} 
                      onChange={e => {}} 
                      isDisabled 
                    />
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <PaymentPlanButton
                      text={t('store_commission_panel.add_required_payment', 'Add required payment')}
                      onClick={handleAddRequiredPayment}
                      disabled={!isEditing}
                      actionName="Add Required Payment"
                      icon={<ExternalLink className="h-4 w-4" />}
                      variant="blue"
                    />
                    {paymentPlan && paymentPlan.items.length > 0 && (
                      <ButtonV2
                        variant="outline"
                        size="sm"
                        onClick={handlePaymentPlanEdit}
                        disabled={!isEditing}
                      >
                        {t('commission_payment_plan.edit') || 'Edit'}
                      </ButtonV2>
                    )}
                  </div>
                  {null}
                  {paymentPlan && paymentPlan.items.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {paymentPlan.items.map((item: PaymentPlanItem, index: number) => (
                          <div key={index} className="border rounded p-3 bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="text-sm text-gray-700">
                                <div className="font-medium">{t(PAYMENT_CONCEPT_LABELS[item.concept as PaymentConcept]) || item.concept}</div>
                                <div className="text-gray-600">{(item.amount || 0).toFixed(2)} {item.currency}</div>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === 'green' ? 'bg-green-100 text-green-800' :
                                    PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === 'red' ? 'bg-red-100 text-red-800' :
                                    PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                    PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === 'blue' ? 'bg-blue-100 text-blue-800' :
                                    PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === 'purple' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {t(PAYMENT_STATUS_LABELS[item.status as PaymentPlanStatus]) || item.status}
                                  </span>
                                </div>
                                {item.dueDate && (
                                  <div className="text-gray-500">{t('commission_payment_plan.due_date', 'Due Date')}: {new Date(item.dueDate).toLocaleDateString()}</div>
                                )}
                                {item.validForDays && (
                                  <div className="text-gray-500">{t('store_commission_panel.commission_payment_plan.valid_for_days', 'Valid for (days)')}: {item.validForDays}</div>
                                )}
                              </div>
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleDeletePaymentSplit(index)}
                                  className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors"
                                  title={t('store_commission_panel.remove_payment_split') || 'Remove payment split'}
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <SectionTitle text={t("store_commission_panel.attachments_title") || "Attachments"} />
              {isEditing && (
                <div className="mb-4">
                  <FileUploadWrapper
                    label={t("store_commission_panel.add_attachments") || "Add attachments"}
                    onChange={files => handleFileChange('attachments', files)}
                    allowedMultipleFiles={true}
                    maxSize={CONFIG.MAX_FILE_UPLOAD}
                    allowedTypes={CONFIG.FILE_TYPES}
                  />
                  {isUploading && (
                    <div className="mt-2 text-sm text-blue-600 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      {t("store_commission_panel.uploading_files") || "Uploading files..."}
                    </div>
                  )}
                </div>
              )}
              {form.attachments && form.attachments.length > 0 ? (
                <ListContainer
                  elements={form.attachments}
                  toElementFn={(file: string, i: number) => (
                    <ElementActions
                      actions={
                        <div>
                          {isEditing && (
                            <IconButton onClick={() => removePhoto('attachments', file)}>
                              <img src={binIcon} alt="Delete" className="w-5 h-5" />
                            </IconButton>
                          )}
                        </div>
                      }
                    >
                      <PrivateResourceLink file={file} type="commission-contracts" prefixId={commission.trackingId} />
                    </ElementActions>
                  )}
                />
              ) : null}
            </div>
            
            {/* Addons section */}
            <div>
              <SectionTitle text={t("store_commission_panel.addons_title") || "Add-ons"} />
              {isEditing && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      const newAddon = {
                        name: '',
                        description: '',
                        cost: 0,
                        currency: 'EUR'
                      };
                      setForm(prev => ({
                        ...prev,
                        addons: [...(prev.addons || []), newAddon]
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm underline mb-4"
                  >
                    {t("store_commission_panel.add_addon") || "+ Add add-on"}
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {form.addons && form.addons.length > 0 ? (
                  form.addons.map((addon, index) => (
                    <div key={index} className="border rounded p-3 bg-gray-50">
                      {isEditing ? (
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <InputShortText
                              label={t("store_commission_panel.addon_name") || "Add-on name"}
                              defaultValue={addon.name}
                              onChange={e => {
                                const newAddons = [...(form.addons || [])];
                                newAddons[index].name = e.target.value;
                                setForm(prev => ({ ...prev, addons: newAddons }));
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <InputShortText
                              label={t("store_commission_panel.addon_description") || "Description"}
                              defaultValue={addon.description}
                              onChange={e => {
                                const newAddons = [...(form.addons || [])];
                                newAddons[index].description = e.target.value;
                                setForm(prev => ({ ...prev, addons: newAddons }));
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <PriceField
                              label={t("store_commission_panel.addon_cost") || "Cost"}
                              currency={addon.currency}
                              price={addon.cost}
                              currencies={CONFIG.SUPPORTED_CURRENCIES}
                              onCurrencyChange={currency => {
                                const newAddons = [...(form.addons || [])];
                                newAddons[index].currency = currency;
                                setForm(prev => ({ ...prev, addons: newAddons }));
                              }}
                              onPriceChange={price => {
                                const newAddons = [...(form.addons || [])];
                                newAddons[index].cost = price;
                                setForm(prev => ({ ...prev, addons: newAddons }));
                              }}
                              isDisabled={false}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newAddons = (form.addons || []).filter((_, i) => i !== index);
                              setForm(prev => ({ ...prev, addons: newAddons }));
                            }}
                            className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors mt-6"
                            title={t("store_commission_panel.remove_addon") || "Remove add-on"}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <div className="flex-1 text-sm font-medium">{addon.name}</div>
                          <div className="flex-1 text-sm">{addon.description || t("store_commission_panel.no_description") || "No description"}</div>
                          <div className="flex-1 text-sm font-medium">{addon.cost} {addon.currency}</div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <NoData message={t("store_commission_panel.no_addons") || "No add-ons"} />
                )}
              </div>
            </div>
            
            
          </div>
          {/* Right: Comments (only if not a new contract) */}
          {commission.id && (
            <div className="w-full md:w-80 flex flex-col">
              <SectionTitle text={t("store_commission_panel.comments_title") || "Comments"} />
              <div className="text-xs text-gray-500 mb-3 px-1">
                {t("store_commission_panel.comments_explanation") || "Comments you add here will be visible to the bride and can help with communication."}
              </div>
              <div className="max-h-80 py-5 px-1 overflow-y-auto space-y-3 mb-2">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <CommentCard 
                      key={comment.id} 
                      comment={comment} 
                      type="commission"
                      isSelf={comment.writtenBy === storeName}
                    />
                  ))
                ) : (
                  <NoData message={t("store_commission_panel.no_comments") || "No comments"} />
                )}
              </div>
              <div className="space-y-2">
                <TextArea
                  label={t("store_commission_panel.add_comment") || "Add comment"}
                  defaultValue={comment}
                  onChange={e => setComment(e.target.value)}
                  isDisabled={isLoading}
                />
                <div className="w-full">
                  <PrimaryButton 
                    text={t("store_commission_panel.add_button") || "Add"} 
                    fn={handleAddComment} 
                    isDisabled={isLoading || !comment.trim()} 
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {isEditing && (
          <div className="mt-4 flex justify-center">
            <PrimaryButton text={t("store_commission_panel.save_button") || "Save"} fn={handleSave} />
          </div>
        )}
        </div>
        {/* Commission Payment Plan Manager SlideOver */}
        <SlideOver
          isOpen={showPaymentPlanManager}
          setIsOpen={setShowPaymentPlanManager}
          title={t('commission_payment_plan.title') || 'Commission Payment Plan'}
          primaryButton={{
            label: t('global.save') || 'Save',
            onClick: () => {
              paymentPlanManagerRef.current?.save();
            }
          }}
          secondaryButton={{
            label: t('global.cancel') || 'Cancel',
            onClick: handlePaymentPlanCancel
          }}
        >
          <CommissionPaymentPlanManager
            ref={paymentPlanManagerRef}
            paymentPlan={paymentPlan}
            onSave={handlePaymentPlanSave}
            onCancel={handlePaymentPlanCancel}
            isEditable={isEditing}
            contractId={commission.id}
          />
        </SlideOver>
        
      </>
    </Modal>
  );
}

export default StoreCommissionPanel; 